/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { useIntl } from 'react-intl';

import Lendyr from '@ibm-watson/shared/assets/images/lendyr.svg';
import { getBranding } from '../../../../ia/util/branding';
import { links } from '../links';
import { messages } from './messages';

export const useResourcesData = () => {
  const { productName } = getBranding();
  const { formatMessage: t } = useIntl();

  const resourcesData = [
    {
      'section-title': t(messages.introductionSectionTitle),
      'section-subTitle': t(messages.introductionSectionSubTitle),
      tiles: [
        {
          'tile-link': links.webchatOverviewTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.webChatOverviewTileTitle),
          'tile-text': t(messages.webChatOverviewTileText, {
            productName
          })
        },
        {
          'tile-link': links.embedWebchatTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.embedWebChatTileTitle),
          'tile-text': t(messages.embedWebChatTileText)
        },
        {
          'tile-link': links.addingLiveAgentTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.addingLiveAgentTileTitle),
          'tile-text': t(messages.addingLiveAgentTileText)
        },
        {
          'tile-link': links.webchatMobileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.addingWebchatToMobileTileTitle),
          'tile-text': t(messages.addingWebchatToMobileTileText)
        },
        {
          'tile-link': links.lendyrDemoLink,
          'tag-title': t(messages.demoSiteTagTitle),
          'tag-type': 'cyan',
          'tile-title': t(messages.lendyrWebchatEssentialsTileTitle),
          'tile-image': Lendyr
        }
      ]
    },
    {
      'section-title': t(messages.outOfTheBoxSectionTitle),
      'section-subTitle': t(messages.outOfTheBoxSectionSubTitle),
      tiles: [
        {
          'tile-link': links.styleWebchatTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.styleWebChatTileTitle),
          'tile-text': t(messages.styleWebChatTileText)
        },
        {
          'tile-link': links.setUpHomeScreenTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.setUpHomeScreenTileTitle),
          'tile-text': t(messages.setUpHomeScreenTileText)
        },
        {
          'tile-link': links.setUpSuggestionTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.setUpSuggestionTileTitle),
          'tile-text': t(messages.setUpSuggestionTileText)
        }
      ]
    },
    {
      'section-title': t(messages.richCustomizationSectionTitle),
      'section-subTitle': t(messages.richCustomizationSectionSubTitle),
      tiles: [
        {
          'tile-link': links.developmentOverviewTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.developmentOverviewTileTitle),
          'tile-text': t(messages.developmentOverviewTileText)
        },
        {
          'tile-link': links.downloadableTranscriptTileLink,
          'tag-title': t(messages.tutorialTagTitle),
          'tag-type': 'green',
          'tile-title': t(messages.downloadableTranscriptTileTitle),
          'tile-text': t(messages.downloadableTranscriptTileText)
        },
        {
          'tile-link': links.implementingCustomButtonsTileLink,
          'tag-title': t(messages.tutorialTagTitle),
          'tag-type': 'green',
          'tile-title': t(messages.implementingCustomButtonsTileTitle),
          'tile-text': t(messages.implementingCustomButtonsTileText)
        },
        {
          'tile-link': links.developContentCarouselLink,
          'tag-title': t(messages.tutorialTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.showingContentCarouselTileTitle),
          'tile-text': t(messages.showingContentCarouselTileText)
        },
        {
          'tile-link': links.lendyrJourneysLink,
          'tag-title': t(messages.demoSiteTagTitle),
          'tag-type': 'cyan',
          'tile-title': t(messages.lendyrJourneysTileTitle),
          'tile-text': t(messages.lendyrJourneysTileText, {
            productName
          })
        },
        {
          'tile-link': links.webchatSetContextLink,
          'tag-title': t(messages.tutorialTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.settingContextVarTileTitle),
          'tile-text': t(messages.settingContextVarTileText)
        },
        {
          'tile-link': links.webchatPageInteractionLink,
          'tag-title': t(messages.tutorialTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.interactingWithWebsiteTileTitle),
          'tile-text': t(messages.interactingWithWebsiteTileText)
        },
        {
          'tile-link': links.lendyrThemingLink,
          'tag-title': t(messages.demoSiteTagTitle),
          'tag-type': 'cyan',
          'tile-title': t(messages.webchaThemingTileTitle),
          'tile-text': t(messages.webchaThemingTileText)
        },
        {
          'tile-link': links.lendyrDeepLinkingLink,
          'tag-title': t(messages.demoSiteTagTitle),
          'tag-type': 'cyan',
          'tile-title': t(messages.deepLinkingTileTitle),
          'tile-text': t(messages.deepLinkingTileText)
        }
      ]
    },
    {
      'section-title': t(messages.administrationSectionTitle),
      'section-subTitle': t(messages.administrationSectionSubTitle),
      tiles: [
        {
          'tile-link': links.supportingGlobalAudiencesTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.supportingGlobalAudiencesTileTitle),
          'tile-text': t(messages.supportingGlobalAudiencesTileText)
        },
        {
          'tile-link': links.versioningTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.versioningTileTitle),
          'tile-text': t(messages.versioningTileText)
        },
        {
          'tile-link': links.securityTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.securityTileTitle),
          'tile-text': t(messages.securityTileText)
        },
        {
          'tile-link': links.userIdentityTileLink,
          'tag-title': t(messages.docsTagTitle),
          'tag-type': 'purple',
          'tile-title': t(messages.userIdentityTileTitle),
          'tile-text': t(messages.userIdentityTileText)
        }
      ]
    }
  ];

  return { resourcesData };
};
